import { useCallback, useState } from "react";
import type { CustomerCreatePayload } from "@/customTypings/customer/Customer";
import type { CustomerDeletedRestoreAvailableConflict } from "@/utils/customer/customerCreateConflict";
import {
    CUSTOMER_CREATE_CONFLICT,
    CustomerCreateConflictError,
    isCustomerDeletedRestoreAvailableConflict,
} from "@/utils/customer/customerCreateConflict";
import { DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE, showToastError, showToastSuccess } from "@/utils";

type useCustomerRestoreOnCreateConflictOptions<TDetail extends { id: string }> = {
    restoreDeletedWithCreateValues: (payload: CustomerCreatePayload & { id: string }) => Promise<TDetail>;
    onRestored: (detail: TDetail) => void;
};

export function useCustomerRestoreOnCreateConflict<TDetail extends { id: string }>({
    restoreDeletedWithCreateValues,
    onRestored,
}: useCustomerRestoreOnCreateConflictOptions<TDetail>) {
    const [restoreConflict, setRestoreConflict] =
        useState<CustomerDeletedRestoreAvailableConflict | null>(null);
    const [pendingRestoreForm, setPendingRestoreForm] = useState<CustomerCreatePayload | null>(null);
    const [restoringDeleted, setRestoringDeleted] = useState(false);

    const handleCreateError = useCallback((err: unknown): boolean => {
        if (err instanceof CustomerCreateConflictError) {
            if (
                isCustomerDeletedRestoreAvailableConflict(
                    err.conflict,
                    CUSTOMER_CREATE_CONFLICT.deletedRestoreAvailable
                )
            ) {
                setRestoreConflict(err.conflict);
                return true;
            }
        }
        return false;
    }, []);

    const clearRestoreConflict = useCallback(() => {
        setRestoreConflict(null);
        setPendingRestoreForm(null);
    }, []);

    const confirmRestore = useCallback(
        async (confirmed: boolean) => {
            if (!confirmed || !restoreConflict || !pendingRestoreForm) {
                clearRestoreConflict();
                return;
            }
            setRestoringDeleted(true);
            try {
                const restored = await restoreDeletedWithCreateValues({
                    id: restoreConflict.existingId,
                    ...pendingRestoreForm,
                });
                const label = restoreConflict.existingName;
                clearRestoreConflict();
                showToastSuccess(`"${label}" restored successfully!`);
                onRestored(restored);
            } catch {
                showToastError(DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE);
            } finally {
                setRestoringDeleted(false);
            }
        },
        [restoreConflict, pendingRestoreForm, restoreDeletedWithCreateValues, onRestored, clearRestoreConflict]
    );

    const restoreModalProps = {
        show: restoreConflict !== null && pendingRestoreForm !== null,
        title: "Restore removed customer?",
        body: restoreConflict
            ? "A removed customer with this name already exists. Restore them with the information you just entered?"
            : "",
        buttonSuccessText: restoringDeleted ? "Restoring…" : "Yes",
        buttonCancelText: "No" as const,
    };

    return {
        handleCreateError,
        setPendingRestoreForm,
        confirmRestore,
        restoreModalProps,
    };
}
